import React from "react";
import { Button, Card, Divider, Space, Typography, message } from "antd";
import moment from "moment";
import useJobPolling, { UseJobPolling } from "hooks/useJobPolling";
import { isActive, PollingIntervals } from "hooks/jobPolling";
import { BatchJob } from "reducers/types";
import JobStatusTag from "components/jobs/JobStatusTag";
import JobApprovalModal from "components/jobs/JobApprovalModal";
import JobWaitBanner from "components/jobs/JobWaitBanner";
import JobQueueList from "components/jobs/JobQueueList";

export type StartJob = (params: object) => Promise<BatchJob | undefined>;

export interface JobCardStartContext {
  /** Starts a job with these params; shows the standard messages (failed / already running / queued). */
  start: StartJob;
  /** The job currently shown (null if the type has no jobs yet). */
  job: BatchJob | null;
}

export interface JobCardProps {
  title: React.ReactNode;
  /** The job type's address, e.g. "/admin/resources/update/tefas/history". */
  basePath: string;
  /** Set for jobs that depend on an external provider: enables the "provider down" banner and approval popup. */
  providerName?: string;
  /** Start controls (form, button, …). */
  start?: (ctx: JobCardStartContext) => React.ReactNode;
  /** How a job is named in the header and in the queued list. Default: its creation time. */
  renderLabel?: (job: BatchJob) => React.ReactNode;
  /** Short progress text next to the status. */
  renderSummary?: (job: BatchJob) => React.ReactNode;
  /** Kind-specific body (item list, progress bar, …). */
  renderBody?: (job: BatchJob, jobs: UseJobPolling) => React.ReactNode;
  polling?: Partial<PollingIntervals>;
}

const defaultLabel = (job: BatchJob) => moment(job.createdAt).format("YYYY-MM-DD HH:mm");

export const failMessage = (text: string) => (ok: boolean) => {
  if (!ok) {
    message.error(text);
  }
};

// Shell shared by every job view: start area, status header, cancel, connection notice, provider-down
// banner + popup, kind-specific body and queued jobs. Specialised by ItemizedJobCard / PlainJobCard.
const JobCard: React.FC<JobCardProps> = ({
  title,
  basePath,
  providerName,
  start,
  renderLabel = defaultLabel,
  renderSummary,
  renderBody,
  polling,
}) => {
  const jobs = useJobPolling({ basePath, ...polling });
  const { job } = jobs;

  const startJob: StartJob = async (params) => {
    const started = await jobs.start(params);
    if (!started) {
      message.error("Could not start the job");
    } else if (started.deduplicated) {
      message.info("The same job is already running — showing it");
    } else if (started.status === "PENDING" && started.queuePosition && started.queuePosition > 1) {
      message.info(`Queued — position ${started.queuePosition}`);
    }
    return started;
  };

  const cancel = (jobId?: string) => jobs.cancel(jobId).then(failMessage("Cancel was not accepted"));
  const approve = () => jobs.approve().then(failMessage("Approval was not accepted"));

  return (
    <Card title={title}>
      {start ? start({ start: startJob, job }) : null}
      {job ? (
        <>
          <Divider style={{ margin: "12px 0" }} />
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
              <Space wrap>
                <span>{renderLabel(job)}</span>
                <JobStatusTag job={job} />
                {renderSummary ? <Typography.Text type="secondary">{renderSummary(job)}</Typography.Text> : null}
              </Space>
              {isActive(job.status) && !job.cancelRequested ? (
                <Button size="small" danger onClick={() => cancel()}>
                  Cancel
                </Button>
              ) : null}
            </Space>
            {jobs.connection === "retrying" ? (
              <Typography.Text type="warning">Connection problem — retrying</Typography.Text>
            ) : null}
            {job.status === "FAILED" && job.error ? <Typography.Text type="danger">{job.error}</Typography.Text> : null}
            {providerName ? (
              <JobWaitBanner job={job} providerName={providerName} onApprove={approve} onCancel={() => cancel()} />
            ) : null}
            {renderBody ? renderBody(job, jobs) : null}
            <JobQueueList jobs={jobs.queued} renderLabel={renderLabel} onCancel={(id) => cancel(id)} />
          </Space>
        </>
      ) : null}
      {providerName ? (
        <JobApprovalModal job={job} providerName={providerName} onApprove={approve} onCancel={() => cancel()} />
      ) : null}
    </Card>
  );
};

export default JobCard;
